import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";

import {
  resolveHubContactSelection,
  searchHubContactSuggestions,
  formatHubContactSuggestionSubtitle,
  type HubContactSelection,
  type HubContactSuggestion,
} from "@/lib/hubContactsApi";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";
import { computeAnchoredListPanel, FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";

const DEBOUNCE_MS = 180;
const MIN_QUERY_LENGTH = 2;
const LIST_MAX_HEIGHT_PX = 224;

export type HubContactDirectorySource = HubContactSuggestion["source"];
export type HubContactRecipient = HubContactSelection;
export type HubContactRecipientSuggestion = HubContactSuggestion;

function useDebouncedValue(value: string, delayMs: number, enabled: boolean): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs, enabled]);
  return enabled ? debounced : "";
}

function recipientKey(recipient: HubContactSelection): string {
  return String(recipient.hubContactId);
}

function recipientChipLabel(recipient: HubContactSelection): string {
  if (recipient.missing) {
    return `Missing contact #${String(recipient.hubContactId)}`;
  }
  const displayName = recipient.displayName?.trim();
  return displayName && displayName.length > 0 ? displayName : recipient.email;
}

function mergeRecipients(
  existing: HubContactSelection[],
  additions: HubContactSelection[],
): HubContactSelection[] {
  const seen = new Set(existing.map(recipientKey));
  const next = [...existing];
  for (const addition of additions) {
    const key = recipientKey(addition);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    next.push(addition);
  }
  return next;
}

function optionDomId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

function suggestionSourceLabel(source: HubContactDirectorySource): string {
  if (source === "staff") {
    return "SGS staff";
  }
  if (source === "hub") {
    return "Hub contact";
  }
  if (source === "freshsales") {
    return "Freshsales";
  }
  if (source === "freshdesk") {
    return "Freshdesk contact";
  }
  return "Freshsales & Freshdesk";
}

export function HubContactTokenInput({
  id: providedId,
  recipients,
  onRecipientsChange,
  fetchSuggestions = searchHubContactSuggestions,
  ensureContact = resolveHubContactSelection,
  freshdeskCompanyId = null,
  allowUnscopedSearch = true,
  placeholder = "Search contacts…",
  disabled = false,
  className,
}: {
  id?: string;
  recipients: HubContactSelection[];
  onRecipientsChange: (recipients: HubContactSelection[]) => void;
  fetchSuggestions?: (query: string) => Promise<HubContactSuggestion[]>;
  ensureContact?: (suggestion: HubContactSuggestion) => Promise<HubContactSelection>;
  freshdeskCompanyId?: number | null;
  allowUnscopedSearch?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}): JSX.Element {
  const generatedId = useId();
  const rootId = providedId ?? generatedId;
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const touchListPick = useTouchListPickSession(open);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [panelBox, setPanelBox] = useState<ReturnType<typeof computeAnchoredListPanel> | null>(null);
  const [ensuring, setEnsuring] = useState(false);

  const trimmedQuery = draft.trim();
  const queryEnabled = open && trimmedQuery.length >= MIN_QUERY_LENGTH;
  const debouncedQuery = useDebouncedValue(trimmedQuery, DEBOUNCE_MS, queryEnabled);

  const scopedSearch =
    freshdeskCompanyId != null && freshdeskCompanyId > 0 && !allowUnscopedSearch;

  const suggestionsQuery = useQuery({
    queryKey: [
      "hub-contact-suggestions",
      debouncedQuery,
      freshdeskCompanyId ?? null,
      allowUnscopedSearch,
    ],
    queryFn: () =>
      scopedSearch || (freshdeskCompanyId != null && freshdeskCompanyId > 0)
        ? searchHubContactSuggestions(debouncedQuery, {
            freshdeskCompanyId,
            unscoped: allowUnscopedSearch,
          })
        : fetchSuggestions(debouncedQuery),
    enabled: queryEnabled && debouncedQuery.length >= MIN_QUERY_LENGTH,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const suggestions = suggestionsQuery.data ?? [];
  const showPanel = open && trimmedQuery.length >= MIN_QUERY_LENGTH;

  function focusInput(): void {
    inputRef.current?.focus();
  }

  function removeRecipient(hubContactId: number): void {
    const key = String(hubContactId);
    onRecipientsChange(recipients.filter((recipient) => recipientKey(recipient) !== key));
    focusInput();
  }

  async function addSuggestion(suggestion: HubContactSuggestion): Promise<void> {
    if (ensuring) {
      return;
    }
    setEnsuring(true);
    try {
      const recipient = await ensureContact(suggestion);
      onRecipientsChange(mergeRecipients(recipients, [recipient]));
      setDraft("");
      setOpen(false);
      setHighlightedIndex(null);
      focusInput();
    } finally {
      setEnsuring(false);
    }
  }

  const repositionPanel = () => {
    const anchor = rootRef.current;
    if (!anchor) {
      setPanelBox(null);
      return;
    }
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: anchor.getBoundingClientRect(),
        listMaxHeightCap: LIST_MAX_HEIGHT_PX,
        minWidth: anchor.offsetWidth,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!showPanel) {
      setPanelBox(null);
      return;
    }
    repositionPanel();
    window.addEventListener("resize", repositionPanel);
    window.addEventListener("scroll", repositionPanel, true);
    return () => {
      window.removeEventListener("resize", repositionPanel);
      window.removeEventListener("scroll", repositionPanel, true);
    };
  }, [showPanel, suggestions.length, suggestionsQuery.isFetching]);

  useEffect(() => {
    if (!showPanel) {
      setHighlightedIndex(null);
      return;
    }
    if (suggestions.length === 0) {
      setHighlightedIndex(null);
      return;
    }
    setHighlightedIndex((current) => (current == null || current >= suggestions.length ? 0 : current));
  }, [showPanel, suggestions]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      return;
    }

    if (event.key === "Backspace" && draft.length === 0 && recipients.length > 0) {
      onRecipientsChange(recipients.slice(0, -1));
      return;
    }

    if (showPanel && suggestions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((current) => {
          if (current == null) {
            return 0;
          }
          return Math.min(current + 1, suggestions.length - 1);
        });
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((current) => {
          if (current == null) {
            return suggestions.length - 1;
          }
          return Math.max(current - 1, 0);
        });
        return;
      }
      if (event.key === "Enter" && highlightedIndex != null) {
        event.preventDefault();
        const suggestion = suggestions[highlightedIndex];
        if (suggestion) {
          void addSuggestion(suggestion);
        }
      }
    }
  }

  const panel = showPanel && panelBox
    ? createPortal(
        <div
          id={listboxId}
          role="listbox"
          className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
          style={{
            position: "fixed",
            top: panelBox.top,
            left: panelBox.left,
            width: panelBox.width,
            zIndex: FLOATING_POPOVER_Z_INDEX,
          }}
        >
          <div className="overflow-y-auto" style={{ maxHeight: panelBox.listContentMaxHeight }}>
            {suggestionsQuery.isFetching && suggestions.length === 0 ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Searching contacts…
              </div>
            ) : null}
            {!suggestionsQuery.isFetching && suggestions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500">No matching contacts.</div>
            ) : null}
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.source}-${suggestion.email}-${String(suggestion.hubContactId ?? "new")}`}
                id={optionDomId(listboxId, index)}
                type="button"
                role="option"
                aria-selected={highlightedIndex === index}
                disabled={ensuring}
                className={cn(
                  "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60",
                  highlightedIndex === index && "bg-slate-50",
                )}
                onPointerDown={(event) => {
                  touchListPick.onListItemPointerDown(event, () => {
                    void addSuggestion(suggestion);
                  });
                }}
              >
                <span className="font-medium text-slate-900">
                  {suggestion.displayName || suggestion.email}
                </span>
                <span className="text-xs text-slate-500">
                  {formatHubContactSuggestionSubtitle(suggestion)} · {suggestionSourceLabel(suggestion.source)}
                </span>
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={rootRef} id={rootId} className={className}>
      <div
        data-bwignore
        data-lpignore="true"
        className={cn(
          "flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500",
          disabled && "cursor-not-allowed bg-slate-100",
        )}
        onClick={() => {
          if (!disabled) {
            focusInput();
          }
        }}
      >
        {recipients.map((recipient) => {
          const label = recipientChipLabel(recipient);
          return (
            <span
              key={recipientKey(recipient)}
              className={cn(
                "inline-flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-xs",
                recipient.missing ? "bg-rose-50 text-rose-800" : "bg-slate-100 text-slate-800",
              )}
              title={recipient.missing ? "This contact no longer exists in the directory." : recipient.email}
            >
              <span className="truncate">{label}</span>
              <button
                type="button"
                className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
                aria-label={`Remove ${label}`}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  removeRecipient(recipient.hubContactId);
                }}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          data-bwignore
          data-lpignore="true"
          disabled={disabled || ensuring}
          value={draft}
          placeholder={recipients.length === 0 ? placeholder : ""}
          aria-label={placeholder}
          className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={showPanel ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            showPanel && highlightedIndex != null ? optionDomId(listboxId, highlightedIndex) : undefined
          }
          onChange={(event) => {
            setDraft(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            window.setTimeout(() => {
              if (touchListPick.shouldYieldBlurCommit()) return;
              setDraft("");
            }, 0);
          }}
        />
      </div>
      {panel}
    </div>
  );
}
